import React, { useState } from 'react'
import { useForm, UseFormRegisterReturn } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { lazy } from 'yup'

import { Card, Flex, InputGroup, Text } from '@clear/design-system'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'

import { Actions, Form, ErrorMessage } from '@components/layout/Form'
import { ContinueButton } from '@components/FormButtons'
import { HeadTitle } from '@components/layout/HeadTitle'
import { PageHeading } from '@components/layout/Heading'
import BiographicsStatusSearchView from '@components/pages/shared/BiographicsForm'
import Divider from '@components/layout/Divider'
import PageContent from '@components/layout/PageContent'
import Spinner from '@components/shared/Spinner'

import { useSessionTimer } from '@hooks/useSessionTimer'
import useEnrollmentApi from '@hooks/useEnrollmentApi'
import useGetLocales from '@hooks/useGetLocales'

import { setFormErrors, extractErrors } from '@utils/apiErrorMessages'
import {
  ApplicationIdKeys,
  applicationIdSearchSchema,
} from '@validators/schema/shared/applicationIdSearchSchema'
import { biographicSearchSchema } from '@validators/schema/shared/biographicSearchSchema'

export type LookupBiographicsFields = Pick<
  Enrollment.BiographicsLookup,
  | 'countryOfBirth'
  | 'dob'
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'noMiddleName'
>
export type LookupApplicationIdFields = Pick<
  Enrollment.ApplicationIdLookup,
  'applicationId'
>

export type LookupFormFields = Partial<
  LookupBiographicsFields & LookupApplicationIdFields
>

const DEFAULT_VALUES: Partial<LookupFormFields> = {
  applicationId: '',
  countryOfBirth: '',
  firstName: '',
  lastName: '',
  middleName: '',
  noMiddleName: false,
}

export type ApplicationIdLookupFormSubmission = {
  type: 'app'
} & Required<LookupApplicationIdFields>
export type BiographicsLookupFormSubmission = {
  type: 'biographics'
} & Required<LookupBiographicsFields>
export type LookupFormSubmission =
  | ApplicationIdLookupFormSubmission
  | BiographicsLookupFormSubmission

type LookUpFormProps = {
  pageTitle: string
  formTitle: string
  formSubtitle: string
  onSubmit: (formData: LookupFormSubmission) => Promise<void>
} & Partial<{
  isSearchableByApplicationId: boolean
}>

function LookUpForm({
  pageTitle,
  formTitle,
  formSubtitle,

  onSubmit,

  isSearchableByApplicationId,
}: LookUpFormProps) {
  const { clearState: clearEnrollmentState } = useEnrollmentApi()
  const { sessionTimeoutHandler } = useSessionTimer(clearEnrollmentState)

  const [countries] = useGetLocales()
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    setValue,
    handleSubmit,
    formState,
    watch,
    setError,
    clearErrors,
  } = useForm<LookupFormFields & { global: string }>({
    mode: 'onChange',
    resolver: yupResolver(
      lazy((value: unknown) => {
        if (
          typeof value === 'object' &&
          value !== null &&
          value['applicationId']
        ) {
          return applicationIdSearchSchema.pick(['applicationId'])
        }

        return biographicSearchSchema
      })
    ),
    defaultValues: DEFAULT_VALUES,
  })

  const {
    applicationId,
    countryOfBirth,
    dob,
    firstName,
    lastName,
    middleName,
    noMiddleName,
  } = watch()

  const areBiographicsFieldsDisabled = isLoading || !!applicationId

  const isApplicationIdFieldDisabled =
    isLoading ||
    !!firstName ||
    !!lastName ||
    !!middleName ||
    !!dob ||
    !!countryOfBirth

  const onFormSubmission = async (formData: LookupFormFields) => {
    clearErrors('global')
    setIsLoading(true)

    /**
     * this fields should be present based on
     * - how this form is setup at this time
     * - the schema
     */
    const data: LookupFormSubmission = formData.applicationId
      ? ({
          type: 'app',
          applicationId: formData.applicationId,
        } as ApplicationIdLookupFormSubmission)
      : ({
          type: 'biographics',
          countryOfBirth: formData.countryOfBirth,
          dob: formData.dob,
          firstName: formData.firstName,
          lastName: formData.lastName,
          middleName: formData.middleName,
          noMiddleName: formData.noMiddleName,
        } as BiographicsLookupFormSubmission)
    try {
      await onSubmit(data)
    } catch (error) {
      if (error.status === 401) {
        sessionTimeoutHandler()
      }
      setFormErrors(await extractErrors(error, formData), setError)
    }

    setIsLoading(false)
  }

  return (
    <PageContent>
      <HeadTitle title={formTitle} description={formSubtitle} />
      <PageHeading>{pageTitle}</PageHeading>
      <Card width="full">
        <Flex
          flexDirection="column"
          justifyContent="space-between"
          minHeight="30.25rem"
        >
          <Form width="full" mb={5}>
            <ApplicationIdSearchView
              show={!!isSearchableByApplicationId}
              disabled={isApplicationIdFieldDisabled}
              errorMessage={formState.errors.applicationId?.message}
              registeredProps={register(ApplicationIdKeys.APPLICATION_ID)}
            />
            <BiographicsStatusSearchView
              register={register}
              setValue={setValue}
              countries={countries}
              state={{
                forceDisable: areBiographicsFieldsDisabled,
                firstName: {
                  errorMessage: formState.errors.firstName?.message,
                },
                middleName: {
                  errorMessage: formState.errors.middleName?.message,
                  disabled: !!noMiddleName,
                },
                lastName: {
                  errorMessage: formState.errors.lastName?.message,
                },
                dob: {
                  errorMessage: formState.errors.dob?.message,
                },
                countryOfBirth: {
                  errorMessage: formState.errors.countryOfBirth?.message,
                },
              }}
            />
          </Form>
          <Actions
            justifyContent="flex-end"
            alignSelf="flex-end"
            width="full"
            mt={[6, 0]}
            mb={6}
          >
            <ContinueButton
              disabled={!formState.isValid}
              onClick={handleSubmit(onFormSubmission)}
              data-cy="continue"
            />
          </Actions>
        </Flex>
        {formState.errors.global &&
          (process.env.NEXT_PUBLIC_APP_ENV === 'test' ||
            process.env.NODE_ENV === 'development') && (
            <ErrorMessage>{formState.errors.global.message}</ErrorMessage>
          )}
      </Card>
      {isLoading && <Spinner />}
    </PageContent>
  )
}

const ApplicationIdSearchView: React.FC<{
  show: boolean
  disabled: boolean
  errorMessage?: string
  registeredProps: UseFormRegisterReturn
}> = ({ show, disabled, errorMessage, registeredProps }) => {
  return show ? (
    <>
      <InputGroup
        {...registeredProps}
        id={ApplicationIdKeys.APPLICATION_ID}
        data-cy={ApplicationIdKeys.APPLICATION_ID}
        name={ApplicationIdKeys.APPLICATION_ID}
        label={'Application ID (Optional)'}
        disabled={disabled}
        error={errorMessage}
      />
      <Flex flexDirection="row" alignItems="center">
        <Divider ml="1rem" />
        <Text variant="caption" minWidth="7.5rem" mx="1.25rem">
          {' '}
          OR SEARCH BY{' '}
        </Text>
        <Divider mr="1rem" />
      </Flex>
    </>
  ) : null
}

export default LookUpForm
